import { ICurrentUser, SubscriptionTier } from '@root/query'

interface IUserDataToGtmAndPixel {
  event: string
  user_id: number
  subscription_tier: SubscriptionTier
  user_data?: {
    sha256_email_address?: string
  }
}

export enum AnalyticEvents {
  LOGIN = 'login',
  REGISTRATION_START = 'registration_start',
  REGISTRATION_BUSINESS_ID_AND_NAME = 'registration_business_id_and_name',
  REGISTRATION_BUSINESS_FORM = 'registration_business_form',
  REGISTRATION_TAX_PERIOD = 'registration_tax_period',
  REGISTRATION_FISCAL_PERIOD = 'registration_fiscal_period',
  REGISTRATION_FINISH = 'registration_finish',
  FIRST_SIGN_UP = 'first_sign_up',
  STRIPE_SESSION_COMPLETE = 'stripe_session_complete',
  MANUAL_INVOICING_REQUESTED = 'manual_invoicing_requested'
}

const ENHANCED_CONVERSION_EVENTS = new Set<AnalyticEvents>([
  AnalyticEvents.FIRST_SIGN_UP,
  AnalyticEvents.STRIPE_SESSION_COMPLETE,
  AnalyticEvents.MANUAL_INVOICING_REQUESTED
])

const normalizeEmailForHashing = (email: string): string => email.trim().toLowerCase()

const sha256Hex = async (value: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(value)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export const hasMarketingConsent = (): boolean =>
  Boolean((window as any)?.Cookiebot?.consent?.marketing)

const sendEventToMetaPixel = (event: AnalyticEvents, payload: object) => {
  if (!hasMarketingConsent()) return
  ;(window as any).fbq?.('trackCustom', event, payload)
}

const buildMetaPixelPayload = (
  user?: ICurrentUser,
  data?: Record<string, unknown>
): Record<string, unknown> => ({
  ...(data ?? {}),
  ...(user ? { subscription_tier: user.subscription_tier } : {})
})

export const sendUserDataToGtmAndPixel = (user: ICurrentUser, event: AnalyticEvents) => {
  try {
    if (!window.dataLayer) return

    window.dataLayer = window.dataLayer || []
    const userData: IUserDataToGtmAndPixel = {
      event,
      user_id: user.id,
      subscription_tier: user.subscription_tier
    }

    window.dataLayer.push(userData)
    sendEventToMetaPixel(event, buildMetaPixelPayload(user))
  } catch (error) {
    console.log('Error in sending GTM and Pixel user data')
    console.log(error)
  }
}

export const sendEventToGtmAndPixel = async (
  event: AnalyticEvents,
  user?: ICurrentUser,
  data?: Record<string, unknown>
) => {
  try {
    if (!window.dataLayer) return

    window.dataLayer = window.dataLayer || []

    const basePayload: Record<string, unknown> = {
      event,
      ...(user ? { user_id: user.id } : {}),
      ...(data ?? {})
    }
    const gtmPayload = { ...basePayload }
    const pixelPayload = buildMetaPixelPayload(user, data)

    // Enhanced Conversions: send hashed user-provided data
    if (
      user?.email &&
      ENHANCED_CONVERSION_EVENTS.has(event) &&
      crypto?.subtle &&
      hasMarketingConsent()
    ) {
      const normalizedEmail = normalizeEmailForHashing(user.email)
      const hashedEmail = await sha256Hex(normalizedEmail)
      gtmPayload['user_data'] = {
        sha256_email_address: hashedEmail
      }
    }

    window.dataLayer.push(gtmPayload)
    sendEventToMetaPixel(event, pixelPayload)
  } catch (error) {
    console.log('Error in sending GTM and Pixel event')
    console.log(error)
  }
}
